import { useCallback, useEffect, useState } from 'react';
import { Share, SharesTitle } from 'types';
import { store } from 'redux/store';
import { getPartnerShares } from '../api/getPartnerShares';
import { getSharesTitlesByShares } from '../api/getSharesTitlesByShares';

type UseGetPartnerShares = {
  partnerId?: string;
  page: number;
  rowsPerPage: number;
  refetchKey?: number;
};

export const useGetPartnerShares = ({
  partnerId,
  page = 1,
  rowsPerPage = 10,
  refetchKey = 0,
}: UseGetPartnerShares) => {
  const [shares, setShares] = useState<Share[]>([]);
  const [totalShares, setTotalShares] = useState<number>(0);
  const [sharesTitles, setSharesTitles] = useState<SharesTitle[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const fetchPartnerShares = useCallback(
    async (controller: AbortController) => {
      if (!partnerId) return;

      try {
        setIsLoading(true);

        const { shares, total } = await store.dispatch(
          getPartnerShares({
            partnerId,
            params: {
              page,
              items: rowsPerPage,
            },
            signal: controller.signal,
          })
        );

        setShares(shares);
        setTotalShares(total);

        const sharesIds = shares.map((share: Share) => share._id);

        const sharesTitles = await store.dispatch(
          getSharesTitlesByShares(sharesIds)
        );

        setSharesTitles(sharesTitles || []);

        if (!controller.signal.aborted) {
          setIsLoading(false);
        }
      } catch (error) {
        if (!controller.signal.aborted) {
          setIsLoading(false);
        }
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [partnerId, page, rowsPerPage, refetchKey]
  );

  useEffect(() => {
    const controller = new AbortController();

    fetchPartnerShares(controller);

    return () => controller.abort();
  }, [fetchPartnerShares]);

  return {
    data: { shares, totalShares, sharesTitles },
    isLoading,
  };
};
